<template>
  <div>
    <v-card-actions>
      <v-spacer></v-spacer>
      <v-btn outlined class="my-4" @click="addCodeDialog = true" :block="isSmallerDisplay()" small elevation="0"
        color="primary">
        <v-icon size="20">mdi-label-percent-outline</v-icon> {{ $t("message.add_promo_code") }}
      </v-btn>
    </v-card-actions>
    <div v-if="loading">
      <v-skeleton-loader type="list-item-avatar, divider, list-item-avatar">
      </v-skeleton-loader>
    </div>
    <div v-else-if="!loading">
      <div class="text-center text-h6 gray--text" v-if="promoCodes.length == 0">{{ $t('message.no_data') }}</div>
      <div v-else>
        <v-data-table :headers="headers" :items="promoCodes" :items-per-page="50" class="elevation-0">
          <template v-slot:item.created="{ item }">
            <v-icon v-if="item.active" size="20" color="success">
              mdi-check-circle-outline
            </v-icon>
            <v-icon v-else size="20" color="error">
              mdi-close-circle-outline
            </v-icon>
            {{ $d(new Date(item.created), "long", $i18n.locale) }}
          </template>
          <template v-slot:item.code="{ item }">
            <div>{{ item.name }}</div>
            <div>
              <b class="mr-1">{{ $t('message.code') }}:</b>
              <v-btn small @click="copyCode(item.code)" text>
                <v-icon size="15" class="mr-1">mdi-content-copy</v-icon>
                {{ item.code }}
              </v-btn>
            </div>
          </template>
          <template v-slot:item.number="{ item }">
            <div class="text-center">
              <span v-if="item.percentOff">
                {{ item.percentOff }}%
              </span>
              <span v-else>
                {{ currency }}{{ item.amountOff }}
              </span>
            </div>
          </template>
          <template v-slot:item.expiresAt="{ item }">
            <span v-if="item.expiresAt">
              {{ $d(new Date(item.expiresAt), "short", $i18n.locale) }}</span>
            <span v-else-if="item.durationInMonths">{{ item.durationInMonths }} {{ $t('message.months') }}</span>
          </template>
          <template v-slot:item.timesRedeemed="{ item }">
            <div class="text-center">
              {{ item.timesRedeemed }}
              <span v-if="item.maxRedemptions" class="ml-1 mr-1">/</span>
              <span v-if="item.maxRedemptions">{{ item.maxRedemptions }}</span>
            </div>
          </template>
          <template v-slot:item.active="{ item }">
            <div class="text-center" v-if="item.active">
              <v-btn small icon @click="promoCode = item; removeCodeDialog = true;">
                <v-icon class="hero-icon-button" size="20">
                  mdi-close-circle-outline
                </v-icon>
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </div>
    </div>
    <v-dialog v-model="addCodeDialog" width="80%">
      <v-card outlined v-if="promoCode">
        <v-card-title class="mb-4">
          {{ $t("message.add_promo_code") }}
        </v-card-title>
        <v-card-text>
          <v-text-field v-model="promoCode.code" dense outlined :rules="codeRule" counter="15"
            :label="$t('message.code')"></v-text-field>
          <v-text-field v-model="promoCode.description" dense outlined counter="100" :label="$t('message.description')">
          </v-text-field>
          <v-text-field :disabled="promoCode.amountOff && promoCode.amountOff > 0 ? true : false"
            v-model="promoCode.percentOff" dense outlined :rules="amountOffRule" :label="$t('message.percent_off')">
          </v-text-field>
          <v-text-field :disabled="promoCode.percentOff && promoCode.percentOff > 0 ? true : false"
            v-model="promoCode.amountOff" dense outlined :rules="amountOffRule" :label="$t('message.amount_off')">
          </v-text-field>
          <v-switch @change="displayDuration()" hide-details class="mt-0" v-model="promoCode.useForSubscriptions"
            :label="$t('message.use_or_subscriptions')"></v-switch>
          <v-switch class="mt-0" v-if="!promoCode.useForSubscriptions" v-model="promoCode.isUnlimited"
            :label="$t('message.unlimited_redemptions')">
          </v-switch>
          <v-text-field :disabled="promoCode.expires" v-if="!promoCode.isUnlimited && !promoCode.useForSubscriptions"
            v-model="promoCode.maxRedemptions" dense outlined :rules="amountOffRule"
            :label="$t('message.max_redemptions')">
          </v-text-field>
          <v-text-field type="number" v-if="!promoCode.isUnlimited && promoCode.useForSubscriptions"
            v-model="promoCode.durationInMonths" dense outlined :rules="amountOffRule"
            :label="$t('message.duration_in_months')"></v-text-field>
          <v-alert type="info" v-if="promoCode.useForSubscriptions" class="text-caption">
            {{ $t('message.promo_code_requiers_duration') }}
          </v-alert>
          <v-switch v-if="!promoCode.useForSubscriptions && !promoCode.isUnlimited" class="mt-0"
            v-model="promoCode.expires" :label="$t('message.expiration_date')"></v-switch>
          <DatePicker style="margin-left:46px" v-if="promoCode.expires && !promoCode.useForSubscriptions"
            :show-time="false" v-model="promoCode.expiration" :past-event="false">
          </DatePicker>
        </v-card-text>
        <v-card-actions>
          <v-btn outlined :disabled="!isReady(promoCode)" @click="addNewPromoCode()" small elevation="0"
            color="primary">
            {{ $t("message.add_promo_code") }}
          </v-btn>
          <v-btn @click="addCodeDialog = false; resetPromoCode()" small elevation="0">
            {{ $t("message.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="removeCodeDialog">
      <v-card outlined>
        <v-card-title>{{ $t("message.deactivate_code") }}</v-card-title>
        <v-card-text>{{ $t("message.are_you_sure") }}</v-card-text>
        <v-card-actions>
          <v-btn @click="removePromoCode()" small elevation="0" color="error">
            {{ $t("message.deactivate_code") }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn @click="removeCodeDialog = false; resetPromoCode()" small elevation="0" color="secondary">
            {{ $t("message.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Api from "@/services/Api";
import DatePicker from "@/components/inputs/DateTimePicker.vue";


export default {
  data() {
    return {
      loading: true,
      promoCodes: [],
      addCodeDialog: null,
      removeCodeDialog: null,
      promoCode: null,
      codeRule: [
        v => (v && v.length <= 15) || this.$t('message.max_characters', [15]),
        v => /^[A-Z0-9]*$/.test(v) || this.$t('message.code_format_invalid')
      ],
      amountOffRule: null,
      currency: '$',
      headers: [
        {
          text: this.$t('message.promo_code'),
          sortable: true,
          value: 'code'
        },
        {
          text: this.$t('message.created'),
          sortable: true,
          value: 'created'
        },
        {
          text: this.$t('message.amount'),
          sortable: true,
          align: 'center',
          value: 'number'
        },
        {
          text: this.$t('message.usage'),
          sortable: true,
          align: 'center',
          value: 'timesRedeemed'
        },
        {
          text: this.$t('message.expires'),
          sortable: true,
          value: 'expiresAt'
        },
        {
          text: "",
          value: 'active'
        },
      ]
    };
  },
  components: {
    DatePicker,
  },
  created() {
    this.amountOffRule = [this.validationRules.number];
    this.resetPromoCode();
    this.initPage();
  },
  methods: {
    displayDuration() {
      if (this.promoCode.useForSubscriptions) {
        this.promoCode.isUnlimited = false;
      }
    },
    copyCode(code) {
      this.copyToClipboard(code);
    },
    getCodes() {
      Api()
        .get("payments/academy/" + this.$store.state.managedAcademy + "/promo/codes")
        .then((response) => {
          if (response && response.data) {
            this.promoCodes = response.data.codes;
            this.currency = response.data.currency;
            this.loading = false;
          }
        });
    },
    removePromoCode() {
      if (this.promoCode && this.promoCode.id) {
        Api()
          .delete("payments/academy/" + this.$store.state.managedAcademy + "/promo/codes/" + this.promoCode.id)
          .then((response) => {
            if (response && response.data) {
              this.getCodes();
              this.removeCodeDialog = false;
              //this.resetPromoCode();
              this.showAlert(
                "success",
                this.$t('message.success')
              );
            }
          }).catch(() => {
            this.showAlert(
              "error",
              this.$t('message.oops_not_working')
            );
          });
      } else {
        this.showAlert(
          "error",
          this.$t('message.oops_not_working')
        );
      }
    },
    addNewPromoCode() {
      if (!this.promoCode.expires) {
        this.expiration = null;
      } else {
        const date = new Date(this.promoCode.expiration);
        date.setHours(23);
        date.setMinutes(59);
        this.promoCode.expiration = date;
      }
      if (this.promoCode.percentOff)
        this.promoCode.percentOff = new Number(this.promoCode.percentOff).valueOf();
      else if (this.promoCode.amountOff)
        this.promoCode.amountOff = new Number(this.promoCode.amountOff).valueOf();
      if (this.promoCode.useForSubscriptions) {
        this.promoCode.expiration = null;
        this.promoCode.maxRedemptions = null;
        if (this.promoCode.isUnlimited)
          this.promoCode.duration = 'forever';
        else if (this.promoCode.durationInMonths)
          this.promoCode.duration = 'repeating';
      } else {
        this.promoCode.duration = null;
        this.promoCode.durationInMonths = null;
      }
      if (this.promoCode.useForSubscriptions && !this.promoCode.durationInMonths) {
        this.showAlert(
          "info",
          this.$t('message.promo_code_requiers_duration')
        );
        return;
      }
      Api()
        .post("payments/academy/" + this.$store.state.managedAcademy + "/promo/codes", this.promoCode)
        .then((response) => {
          if (response && response.data) {
            this.getCodes();
            this.addCodeDialog = false;
            //this.resetPromoCode();
            this.showAlert(
              "success",
              this.$t('message.success')
            );
          }
        }).catch(() => {
          this.showAlert(
            "error",
            this.$t('message.oops_not_working')
          );
        });
    },
    resetPromoCode() {
      this.promoCode = {
        percentOff: 0,
        amountOff: 0,
        description: null,
        code: null,
        expires: false,
        maxRedemptions: 0,
        isUnlimited: true,
        expiration: null
      };
    },
    initPage() {
      this.getCodes();
    },
    isReady(promoCode) {
      if (promoCode && promoCode.code && (promoCode.percentOff > 0 || promoCode.amountOff > 0))
        return true;
      else
        return false;
    }
  },
};
</script>

<style scoped></style>